import type EventStream from 'Models/EventStream';
import type Phrase from 'Models/Phrase';
import { PhraseEndReason } from 'Types/EventTypes';

// MUS-646: a Sight Reading phrase that ended without a completion is recorded
// as a SessionInterruptionEvent carrying the reason it ended. A phrase gets
// either a PhraseCompletedEvent or one of these, never both, because ending a
// phrase marks it complete the way a completion does.

// The session a phrase was played in, captured when play starts.
export type PhraseEndContext = {
  playSessionId: string;
  levelNumber: number;
  tierNumber: number;
};

export type PhraseEnd = PhraseEndContext & {
  phraseS3Id: string;
  position?: number;
  reason: PhraseEndReason;
};

// A pass of two MIDI events (one key press and its release) or fewer is not a
// completion: the phrase played through with next to nothing played.
export const NEAR_EMPTY_PASS_MAX_MIDI_EVENTS = 2;

export const isNearEmptyPass = (midiEventCount: number): boolean =>
  midiEventCount <= NEAR_EMPTY_PASS_MAX_MIDI_EVENTS;

type PhraseEndStream = Pick<EventStream, 'getStartedPhraseInProgress' | 'getPhraseOwedCompletion'>;

// Takes the phrase still owed a completion (one played into whose last window
// has not closed yet) off the stream before it is reset, and marks it complete
// so nothing ends it. The caller sends its completion from the returned phrase.
export const takePhraseOwedCompletion = (
  stream: Pick<EventStream, 'getPhraseOwedCompletion'>,
): Phrase | null => {
  const owed = stream.getPhraseOwedCompletion();
  if (!owed) return null;
  owed.phrase.setComplete(true);
  return owed.phrase;
};

// Ends the phrase the player is in, for a reason that ends it for good (leaving
// the screen, the tier ending, a new session), and the phrase before it when
// that one is still owed a completion (just after a bar line, when its last
// note's window has not closed). Returns the records to send, none when there
// is nothing to end: no session, no phrase started yet, or the phrases already
// completed or ended. A tier end is recorded only on a phrase played into
// (position > 0): play stops there, so the next phrase at its start was never
// asked for. Marks each ended phrase complete so a completion can never follow.
export const endPhrasesInProgress = (
  stream: PhraseEndStream,
  context: PhraseEndContext | null,
  reason: PhraseEndReason,
): PhraseEnd[] => {
  if (!context) return [];
  const owed = stream.getPhraseOwedCompletion();
  const inProgress = stream.getStartedPhraseInProgress();
  const candidates = [owed, inProgress].filter(
    (candidate, index, all): candidate is { phrase: Phrase; position: number } =>
      candidate !== undefined &&
      !candidate.phrase.getComplete() &&
      (reason !== PhraseEndReason.TierEnd || candidate.position > 0) &&
      all.findIndex((other) => other?.phrase === candidate.phrase) === index,
  );
  return candidates.map(({ phrase, position }) => {
    phrase.setComplete(true);
    return { ...context, phraseS3Id: phrase.uuid, position, reason };
  });
};
